import orderService from "@/services/orderService";
import stockService from "@/services/stockService";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Filtros de período da home */
export const PERIODS = [
  { id: "today", label: "Hoje", ordersLabel: "Pedidos Hoje", compareLabel: "ontem" },
  { id: "yesterday", label: "Ontem", ordersLabel: "Pedidos Ontem", compareLabel: "anteontem" },
  { id: "week", label: "Esta Semana", ordersLabel: "Pedidos na Semana", compareLabel: "a semana passada" },
  { id: "month", label: "Mês", ordersLabel: "Pedidos no Mês", compareLabel: "o mês passado" },
];

/** Status ainda em andamento, na ordem do fluxo do pedido */
export const ACTIVE_STATUSES = ["Aguardando", "Preparando", "Liberado", "Entregue"];

/** Próximo passo de cada status (ação principal do card) */
export const NEXT_STATUS = {
  Aguardando: { status: "Preparando", label: "Aceitar" },
  Preparando: { status: "Liberado", label: "Liberar" },
  Liberado: { status: "Entregue", label: "Entregue" },
  Entregue: { status: "Finalizado", label: "Concluir" },
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Intervalo do período e o intervalo anterior equivalente. Nos períodos em
 * andamento (hoje, semana, mês) a comparação usa o mesmo tempo decorrido
 * (ex.: hoje até agora × ontem até a mesma hora).
 */
export const getPeriodRange = (periodId, now = new Date()) => {
  const today = startOfDay(now);
  const elapsed = (start) => now.getTime() - start.getTime();

  switch (periodId) {
    case "yesterday": {
      const start = new Date(today.getTime() - DAY_MS);
      return {
        start,
        end: today,
        prevStart: new Date(start.getTime() - DAY_MS),
        prevEnd: start,
      };
    }
    case "week": {
      // Semana comercial começando na segunda-feira
      const offset = (today.getDay() + 6) % 7;
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
      const prevStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 7);
      return {
        start,
        end: null,
        prevStart,
        prevEnd: new Date(prevStart.getTime() + elapsed(start)),
      };
    }
    case "month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prevEnd = new Date(
        Math.min(prevStart.getTime() + elapsed(start), start.getTime()),
      );
      return { start, end: null, prevStart, prevEnd };
    }
    case "today":
    default:
      return {
        start: today,
        end: null,
        prevStart: new Date(today.getTime() - DAY_MS),
        prevEnd: new Date(today.getTime() - DAY_MS + elapsed(today)),
      };
  }
};

const inRange = (order, start, end) => {
  const date = orderService.parseDateTime(order?.date_time);
  if (!date) return false;
  return date >= start && (!end || date < end);
};

const summarize = (orders) => {
  const valid = orders.filter((order) => order.status !== "Cancelado");
  const revenue = valid.reduce((sum, order) => sum + orderService.calculateTotal(order), 0);
  const paid = valid
    .filter((order) => order.payed === true)
    .reduce((sum, order) => sum + orderService.calculateTotal(order), 0);

  return {
    revenue,
    paid,
    count: valid.length,
    completed: valid.filter((o) => o.status === "Entregue" || o.status === "Finalizado").length,
    inProgress: valid.filter((o) => ["Aguardando", "Preparando", "Liberado"].includes(o.status)).length,
    cancelled: orders.length - valid.length,
    averageTicket: valid.length ? revenue / valid.length : 0,
  };
};

/**
 * Indicadores do período (faturamento, pedidos, ticket médio...) e a
 * variação do faturamento em relação ao período anterior.
 */
export const computeMetrics = (orders, periodId, now = new Date()) => {
  const range = getPeriodRange(periodId, now);
  const current = summarize(orders.filter((o) => inRange(o, range.start, range.end)));
  const previous = summarize(orders.filter((o) => inRange(o, range.prevStart, range.prevEnd)));

  return {
    ...current,
    previousRevenue: previous.revenue,
    revenueChange:
      previous.revenue > 0
        ? ((current.revenue - previous.revenue) / previous.revenue) * 100
        : null,
    paidRatio: current.revenue > 0 ? Math.min(1, current.paid / current.revenue) : 0,
  };
};

/** Pedidos em andamento: primeiro os que pedem ação, depois os mais antigos */
export const sortActiveOrders = (orders) =>
  orders
    .filter((order) => ACTIVE_STATUSES.includes(order.status))
    .sort((a, b) => {
      const byStatus = ACTIVE_STATUSES.indexOf(a.status) - ACTIVE_STATUSES.indexOf(b.status);
      if (byStatus !== 0) return byStatus;
      const time = (o) => orderService.parseDateTime(o.date_time)?.getTime() || 0;
      return time(a) - time(b);
    });

/** "1x Tapioca Carne Seca + 1x Suco Laranja" */
export const summarizeItems = (order) =>
  (order?.products || [])
    .map((item) => `${item.quantity || 1}x ${item.product?.name || "Produto"}`)
    .join(" + ");

/**
 * Produtos com estoque esgotado ou abaixo do mínimo (lotes somados por
 * produto: disponível = atual − reservado).
 * @returns {Array<{name, available, min, soldOut}>}
 */
export const getStockAlerts = (stocks) => {
  const byProduct = new Map();
  stocks.forEach((lot) => {
    const key = lot.product_id || lot.product_code || lot.product_name;
    if (!key) return;
    const entry = byProduct.get(key) || {
      name: lot.product_name || "Produto",
      available: 0,
      min: 0,
    };
    entry.available += stockService.calculateAvailableQuantity(
      lot.current_quantity,
      lot.reserved_quantity,
    );
    entry.min = Math.max(entry.min, Number(lot.min_quantity) || 0);
    byProduct.set(key, entry);
  });

  return [...byProduct.values()]
    .filter((item) => item.available <= 0 || item.available <= item.min)
    .map((item) => ({ ...item, soldOut: item.available <= 0 }))
    .sort((a, b) => a.available - b.available);
};
